
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useSupportTicket } from '@/hooks/useSupportTickets';
import { useAuthors } from '@/hooks/useAuthors';
import TicketConversation from '@/components/admin/TicketConversation';
import TicketInformation from '@/components/admin/TicketInformation';

const AdminTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading } = useSupportTicket(id!);
  const { data: authors } = useAuthors();
  const [status, setStatus] = useState<string>('');
  
  const author = authors?.find(a => ticket && a.id === ticket.authorId);
  
  // Initialize status from ticket when data is loaded
  React.useEffect(() => {
    if (ticket && !status) {
      setStatus(ticket.status);
    }
  }, [ticket, status]);

  const handleTicketDeleted = () => {
    // Navigate back to support tickets list after successful deletion
    navigate('/admin/support');
  };

  if (isLoading) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex justify-center items-center h-full">
          <p>Loading ticket details...</p>
        </div>
      </MainLayout>
    );
  }

  if (!ticket) {
    return (
      <MainLayout allowedRoles={['admin']}>
        <div className="flex flex-col items-center justify-center h-full">
          <h2 className="text-2xl font-bold mb-4">Ticket Not Found</h2>
          <Button onClick={() => navigate('/admin/support')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Support Tickets
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout allowedRoles={['admin']}>
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/admin/support')}
          className="mr-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <PageHeader
          title={ticket.displayId || `Ticket #${id?.slice(0, 8)}`}
          description={ticket.subject}
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket Messages */}
        <div className="lg:col-span-2 space-y-6">
          <TicketConversation ticket={ticket} author={author} />
        </div>
        
        {/* Ticket Details */}
        <div className="space-y-6">
          <TicketInformation 
            ticket={ticket} 
            author={author} 
            status={status} 
            setStatus={setStatus}
            onTicketDeleted={handleTicketDeleted}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminTicketDetail;
